/**
 * SearchSpring Mobile Filter Drawer
 *
 * Uses Horizon's <dialog-component> for drawer behavior (showModal,
 * scroll lock, click-outside, escape, animations). Matches the native
 * filter drawer structure from filters.liquid lines 359-504.
 *
 * Loaded as standalone script by ss-collection.liquid and ss-search-results.liquid.
 * Depends on: window.SSURLState, window.__ssCollection or window.__ssSearch, <dialog-component>
 */

(function () {
  'use strict';

  // Horizon's icon-close.svg
  const CLOSE_SVG =
    '<span class="svg-wrapper svg-wrapper--small">' +
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  // Internal scoping field (sent as bgfilter on collection pages) — never a
  // shopper-facing filter. Matches the exclusion in ss-filters.js #renderFacets.
  const HIDDEN_FACET_FIELD = 'collection_handle';

  // Horizon's icon-caret.svg
  const CARET_SVG =
    '<span class="svg-wrapper icon-caret icon-animated">' +
    '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg></span>';

  // Horizon's icon-filters-close.svg (for active filter pills)
  const FILTERS_CLOSE_SVG =
    '<span class="svg-wrapper svg-wrapper--smaller">' +
    '<svg width="7" height="8" viewBox="0 0 7 8" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<g opacity="var(--close-icon-opacity, 1)">' +
    '<path d="M6 1.5L1 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/>' +
    '<path d="M1 1.5L6 6.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round"/>' +
    '</g></svg></span>';

  // Horizon's double-sided-caret icon (from snippets/icon.liquid)
  const SELECT_CARET_SVG =
    '<svg aria-hidden="true" focusable="false" class="icon icon-caret" width="8" height="13" viewBox="0 0 8 13">' +
    '<g opacity="0.6">' +
    '<path d="M4.93509 1.08583C4.44566 0.596391 3.65213 0.596391 3.16269 1.08583L0.509308 3.73921C0.215647 4.03287 0.215647 4.50899 0.509308 4.80265C0.802969 5.09631 1.27909 5.09631 1.57275 4.80265L4.04889 2.32651L6.52504 4.80265C6.8187 5.09631 7.29482 5.09631 7.58848 4.80265C7.88214 4.50899 7.88214 4.03287 7.58848 3.73921L4.93509 1.08583Z" fill="#333030"/>' +
    '<path d="M7.58848 9.81591L4.93509 12.4693C4.44566 12.9587 3.65213 12.9587 3.16269 12.4693L0.509308 9.81591C0.215647 9.52225 0.215647 9.04613 0.509308 8.75247C0.802969 8.45881 1.27909 8.45881 1.57275 8.75247L4.04889 11.2286L6.52504 8.75247C6.8187 8.45881 7.29482 8.45881 7.58848 8.75247C7.88214 9.04613 7.88214 9.52225 7.58848 9.81591Z" fill="#333030"/>' +
    '</g></svg>';

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  // Athos returns HTML-encoded text; decode before re-escaping.
  function decodeEntities(str) {
    const txt = document.createElement('textarea');
    txt.innerHTML = String(str);
    return txt.value;
  }

  function escapeHtml(str) {
    if (!str) return '';
    return decodeEntities(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    if (!str) return '';
    return decodeEntities(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  let colorMap = null;

  function getColorMap() {
    if (colorMap !== null) return colorMap;
    colorMap = {};
    const el = document.getElementById('ss-color-map');
    if (el) {
      try { colorMap = JSON.parse(el.textContent); } catch { /* ignore */ }
    }
    return colorMap;
  }

  function getColorValue(colorName) {
    const map = getColorMap();
    const normalized = colorName.toLowerCase().trim();
    return map[normalized] || map[colorName] || null;
  }

  function sortValue(option) {
    return (option.field || 'relevance') + '_' + (option.direction || 'asc');
  }

  function getCurrentSort() {
    const params = window.SSURLState.readFromURL();
    for (const [key, value] of Object.entries(params)) {
      if (key.startsWith('sort.')) {
        return key.replace('sort.', '') + '_' + value;
      }
    }
    return null;
  }

  // ---------------------------------------------------------------------------
  // SSMobileDrawer
  // ---------------------------------------------------------------------------

  class SSMobileDrawer {
    #dialogComponent = null;
    #dialogEl = null;
    #bodyEl = null;
    #titleEl = null;
    #footerBtn = null;
    #clearBtn = null;
    #activePillsEl = null;
    #lastResponse = null;
    #priceDebounceTimer = null;

    constructor() {
      this.#buildDrawer();
      this.#bindEvents();
    }

    // -----------------------------------------------------------------------
    // DOM — mirrors Horizon's filters.liquid lines 359-504
    // Uses <dialog-component> wrapping a native <dialog>
    // -----------------------------------------------------------------------

    #buildDrawer() {
      // Outer: <dialog-component> — Horizon's reusable dialog controller
      this.#dialogComponent = document.createElement('dialog-component');
      this.#dialogComponent.id = 'ss-filters-drawer';
      this.#dialogComponent.className = 'facets-block-wrapper facets-block-wrapper--vertical';

      // Inner: native <dialog> with Horizon's drawer classes
      // dialog-drawer--right = left-side drawer in Horizon's convention
      this.#dialogComponent.innerHTML =
        '<dialog ref="dialog" class="facets facets--drawer dialog-modal drawer dialog-drawer dialog-drawer--right spacing-style" aria-labelledby="ss-filters-drawer-heading">' +
          '<div class="facets__form-wrapper facets-drawer__form-wrapper">' +
            '<div class="facets__form">' +
              // Header — facets__title-wrapper (line 383-416)
              '<div class="facets__title-wrapper" style="--color-shadow: rgb(var(--color-foreground-rgb) / var(--opacity-10-25));">' +
                '<h2 id="ss-filters-drawer-heading" class="facets-drawer__title h3"></h2>' +
                '<button type="button" class="button facets-drawer__close button-unstyled close-button" data-action="close">' +
                  CLOSE_SVG +
                '</button>' +
              '</div>' +
              // Scrollable content — facets-drawer__filters (line 419-470)
              // Sort first (above filters), matching native drawer order.
              '<div class="facets-drawer__filters ss-drawer-scroll">' +
                '<div class="sorting-filter ss-drawer-sort-container"></div>' +
                '<div class="facets-remove facets-remove--mobile-and-vertical ss-drawer-pills"></div>' +
                '<div class="facets__filters-wrapper ss-drawer-facets"></div>' +
              '</div>' +
            '</div>' +
          '</div>' +
          // Footer — facets__drawer-actions (line 474-501)
          '<div class="facets__drawer-actions">' +
            '<button type="button" class="button-secondary facets__clear-all ss-drawer-clear-all" data-action="clear">Clear all</button>' +
            '<button type="button" class="button facets__see-results ss-drawer-apply" data-action="close">See items</button>' +
          '</div>' +
        '</dialog>';

      this.#dialogEl = this.#dialogComponent.querySelector('dialog');
      this.#bodyEl = this.#dialogComponent.querySelector('.ss-drawer-scroll');
      this.#titleEl = this.#dialogComponent.querySelector('.facets-drawer__title');
      this.#activePillsEl = this.#dialogComponent.querySelector('.ss-drawer-pills');
      this.#footerBtn = this.#dialogComponent.querySelector('.ss-drawer-apply');
      this.#clearBtn = this.#dialogComponent.querySelector('.ss-drawer-clear-all');

      document.body.appendChild(this.#dialogComponent);
    }

    // -----------------------------------------------------------------------
    // Events
    // -----------------------------------------------------------------------

    #bindEvents() {
      // Close button and "See results" — use dialog-component's closeDialog
      this.#dialogComponent.addEventListener('click', (e) => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (action === 'close') this.#dialogComponent.closeDialog();
        if (action === 'clear') this.#clearAll();
      });

      // Filter/sort changes (delegated on scrollable body)
      this.#bodyEl.addEventListener('change', (e) => this.#handleChange(e));
      this.#bodyEl.addEventListener('input', (e) => this.#handlePriceInput(e));

      // Pill remove buttons
      this.#activePillsEl.addEventListener('click', (e) => this.#handlePillRemove(e));

      // Open drawer on trigger button click
      document.addEventListener('click', (e) => {
        if (e.target.closest('.ss-mobile-filter-trigger')) {
          e.preventDefault();
          this.open();
        }
      });
    }

    // -----------------------------------------------------------------------
    // Public API
    // -----------------------------------------------------------------------

    onResultsRendered(response) {
      this.#lastResponse = response;
      this.#updateFooterCount(response);
      this.#updateTriggerBadge(response);
      this.#updateClearAllVisibility(response.filterSummary || []);
      if (this.#dialogEl.open) {
        this.#renderActivePills(response.filterSummary || []);
        this.#updateTitle(response.filterSummary || []);
      }
    }

    open() {
      if (!this.#lastResponse) return;
      this.#renderContent();
      this.#dialogComponent.showDialog();
    }

    // -----------------------------------------------------------------------
    // Render drawer content
    // -----------------------------------------------------------------------

    #renderContent() {
      const response = this.#lastResponse;
      if (!response) return;

      this.#updateTitle(response.filterSummary || []);
      this.#renderActivePills(response.filterSummary || []);

      // Facets (vertical style, open by default)
      const facetsEl = this.#dialogComponent.querySelector('.ss-drawer-facets');
      if (facetsEl && response.facets?.length > 0) {
        facetsEl.innerHTML = response.facets
          .filter((facet) => facet.field !== HIDDEN_FACET_FIELD)
          .map((facet) => {
            if (facet.type === 'range' || facet.field === 'ss_price') {
              return this.#renderPriceRangeFacet(facet);
            }
            if (/color|colour/i.test(facet.field) || /color|colour/i.test(facet.label)) {
              return this.#renderSwatchFacet(facet);
            }
            return this.#renderListFacet(facet);
          })
          .join('');
      }

      // Sort (native <select>)
      const sortEl = this.#dialogComponent.querySelector('.ss-drawer-sort-container');
      if (sortEl && response.sorting?.options?.length > 0) {
        sortEl.innerHTML = this.#renderSort(response.sorting.options);
      }

      this.#updateFooterCount(response);
    }

    #updateTitle(filterSummary) {
      if (!this.#titleEl) return;
      const count = (filterSummary || []).length;
      this.#titleEl.innerHTML = 'Filter' +
        (count > 0 ? ' <span class="bubble facets__bubble">' + count + '</span>' : '');
    }

    // -----------------------------------------------------------------------
    // Active filter pills
    // -----------------------------------------------------------------------

    #renderActivePills(filterSummary) {
      if (!this.#activePillsEl) return;
      if (!filterSummary || filterSummary.length === 0) {
        this.#activePillsEl.innerHTML = '';
        return;
      }
      this.#activePillsEl.innerHTML = filterSummary
        .filter((f) => f.field !== HIDDEN_FACET_FIELD)
        .map(
          (f) =>
            '<span class="pills__pill pills__pill--desktop-small facets-remove__pill" ' +
            'tabindex="0" role="button" ' +
            'data-filter-field="' + escapeAttr(f.field) + '" ' +
            'data-filter-value="' + escapeAttr(f.value) + '">' +
            escapeHtml(f.label || f.value) +
            FILTERS_CLOSE_SVG +
            '<span class="visually-hidden">Remove</span>' +
            '</span>'
        )
        .join('');
    }

    // -----------------------------------------------------------------------
    // Sort — native <select>
    // -----------------------------------------------------------------------

    #renderSort(options) {
      const current = getCurrentSort();
      const currentValue = current || sortValue(options[0]);

      const optionsHtml = options
        .map((opt) => {
          const val = sortValue(opt);
          const label = opt.label || val;
          return (
            '<option value="' + escapeAttr(val) + '"' +
            (val === currentValue ? ' selected' : '') +
            '>' + escapeHtml(label) + '</option>'
          );
        })
        .join('');

      return (
        '<div class="sorting-filter__container">' +
        '<label class="facets__label" style="font-size: var(--font-h4--size);">Sort</label>' +
        '<div class="sorting-filter__select-wrapper">' +
        '<select class="sorting-filter__select ss-drawer-sort-select">' +
        optionsHtml +
        '</select>' +
        SELECT_CARET_SVG +
        '</div>' +
        '</div>'
      );
    }

    // -----------------------------------------------------------------------
    // Facet rendering — vertical layout, open by default
    // -----------------------------------------------------------------------

    #renderListFacet(facet) {
      const shouldUsePills = facet.values && facet.values.every((v) => (v.label || v.value || '').length <= 3);

      const items = (facet.values || [])
        .map((val, i) => {
          const isDisabled = val.count === 0 && !val.active;
          const inputId = 'SSDrawer-' + escapeAttr(facet.field) + '-' + i;
          const disabledClass = isDisabled ? ' facets__inputs-list-item--disabled' : '';

          if (shouldUsePills) {
            return (
              '<li class="facets__inputs-list-item' + disabledClass + '">' +
              '<div class="facets__pill-wrapper">' +
              '<input type="checkbox" class="facets__pill-input"' +
              ' name="filter.' + escapeAttr(facet.field) + '"' +
              ' value="' + escapeAttr(val.value) + '"' +
              ' id="' + inputId + '"' +
              ' data-label="' + escapeAttr(val.label || val.value) + '"' +
              (val.active ? ' checked' : '') +
              (isDisabled ? ' disabled' : '') +
              '>' +
              '<label class="facets__pill-label" for="' + inputId + '">' +
              escapeHtml(val.label || val.value) +
              '</label>' +
              '</div></li>'
            );
          }

          return (
            '<li class="facets__inputs-list-item' + disabledClass + '">' +
            '<div class="checkbox' + (isDisabled ? ' checkbox--disabled' : '') + '">' +
            '<input type="checkbox" class="checkbox__input"' +
            ' name="filter.' + escapeAttr(facet.field) + '"' +
            ' value="' + escapeAttr(val.value) + '"' +
            ' id="' + inputId + '"' +
            ' data-label="' + escapeAttr(val.label || val.value) + '"' +
            (val.active ? ' checked' : '') +
            (isDisabled ? ' disabled' : '') +
            '>' +
            '<label class="checkbox__label" for="' + inputId + '">' +
            '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" class="icon-checkmark">' +
            '<path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1.5)" stroke-linecap="round" stroke-linejoin="round"/>' +
            '</svg>' +
            '<span class="checkbox__label-text">' + escapeHtml(val.label || val.value) + '</span>' +
            '</label>' +
            '</div></li>'
          );
        })
        .join('');

      const listClass = shouldUsePills
        ? 'facets__inputs-list facets__inputs-list--grid list-unstyled'
        : 'facets__inputs-list list-unstyled';
      const wrapperClass = shouldUsePills
        ? 'facets__inputs-wrapper facets__inputs-wrapper--row'
        : 'facets__inputs-wrapper';

      return this.#wrapInAccordion(
        facet.label || facet.field,
        '<div class="' + wrapperClass + '"><ul class="' + listClass + '">' + items + '</ul></div>',
        facet.field
      );
    }

    #renderSwatchFacet(facet) {
      const items = (facet.values || [])
        .map((val, i) => {
          const isDisabled = val.count === 0 && !val.active;
          const colorValue = getColorValue(val.label || val.value);
          const inputId = 'SSDrawer-' + escapeAttr(facet.field) + '-' + i;
          const disabledClass = isDisabled ? ' facets__inputs-list-item--disabled' : '';

          let swatchStyle = '--swatch-background: #ccc;';
          if (colorValue) {
            if (colorValue.startsWith('http') || colorValue.startsWith('/')) {
              swatchStyle = '--swatch-background: url(' + escapeAttr(colorValue) + ');';
            } else {
              swatchStyle = '--swatch-background: ' + escapeAttr(colorValue) + ';';
            }
          }

          return (
            '<li class="facets__inputs-list-item' + disabledClass + '">' +
            '<fieldset class="variant-option variant-option--buttons variant-option--swatches' +
            (isDisabled ? ' variant-option--swatches-disabled' : '') + '"' +
            ' aria-label="' + escapeAttr(val.label || val.value) + '">' +
            '<label class="variant-option__button-label variant-option__button-label--has-swatch">' +
            '<div class="variant-option__swatch-wrapper">' +
            '<input tabindex="0" type="checkbox"' +
            ' name="filter.' + escapeAttr(facet.field) + '"' +
            ' value="' + escapeAttr(val.value) + '"' +
            ' id="' + inputId + '"' +
            ' aria-label="' + escapeAttr(val.label || val.value) + '"' +
            (val.active ? ' checked' : '') +
            (isDisabled ? ' disabled' : '') +
            '>' +
            '<span class="swatch swatch--filter" style="' + swatchStyle + '"></span>' +
            '</div>' +
            '<label class="facets__swatch-label" for="' + inputId + '">' +
            escapeHtml(val.label || val.value) +
            '</label>' +
            '</label>' +
            '</fieldset></li>'
          );
        })
        .join('');

      let swatchColumns = (facet.values || []).length;
      if (swatchColumns > 4) {
        swatchColumns = 4;
        const mod4 = (facet.values || []).length % 4;
        const mod3 = (facet.values || []).length % 3;
        if (mod4 !== 0 && mod4 !== 3) {
          if (mod3 === 0 || mod3 === 2) swatchColumns = 3;
        }
      }

      return this.#wrapInAccordion(
        facet.label || facet.field,
        '<div class="facets__inputs-wrapper facets__inputs-wrapper--row">' +
        '<ul class="facets__inputs-list facets__inputs-list--swatches facets__inputs-list--swatches-grid list-unstyled" style="--swatch-columns: ' + swatchColumns + ';">' +
        items + '</ul></div>',
        facet.field
      );
    }

    #renderPriceRangeFacet(facet) {
      const activeMin = facet.active ? facet.active.low : '';
      const activeMax = facet.active ? facet.active.high : '';
      const rangeMax = facet.range ? facet.range.high : '';

      const content =
        '<div class="facets__inputs-wrapper facets__inputs-wrapper--row">' +
        '<div class="price-facet">' +
        '<div class="price-facet__inputs-wrapper">' +
        '<div class="field price-facet__field">' +
        '<input class="field__input price-facet__input" type="text" inputmode="decimal"' +
        ' name="filter.ss_price.low" placeholder="0"' +
        ' data-price-range="min"' +
        (activeMin ? ' value="' + escapeAttr(String(activeMin)) + '"' : '') +
        ' autocomplete="off">' +
        '<label class="field__label price-facet__label">$</label>' +
        '</div>' +
        '<div class="price-facet__separator">to</div>' +
        '<div class="field price-facet__field">' +
        '<input class="field__input price-facet__input" type="text" inputmode="decimal"' +
        ' name="filter.ss_price.high" placeholder="' + escapeAttr(String(rangeMax)) + '"' +
        ' data-price-range="max"' +
        (activeMax ? ' value="' + escapeAttr(String(activeMax)) + '"' : '') +
        ' autocomplete="off">' +
        '<label class="field__label price-facet__label">$</label>' +
        '</div>' +
        '</div>' +
        (rangeMax ? '<div class="price-facet__highest-price">The highest price is $' + escapeHtml(String(rangeMax)) + '</div>' : '') +
        '</div></div>';

      return this.#wrapInAccordion(
        facet.label || 'Price',
        content,
        facet.field || 'ss_price'
      );
    }

    #wrapInAccordion(label, content, field) {
      return (
        '<accordion-custom class="facets__item" open open-by-default-on-mobile open-by-default-on-desktop>' +
        '<details class="facets__panel" open>' +
        '<summary class="facets__summary">' +
        '<span class="facets__label">' + escapeHtml(label) + '</span>' +
        '<div class="facets__status-wrapper">' +
        CARET_SVG +
        '</div>' +
        '</summary>' +
        '<floating-panel-component class="facets__inputs facets__panel-content details-content">' +
        content +
        '</floating-panel-component>' +
        '</details>' +
        '</accordion-custom>'
      );
    }

    // -----------------------------------------------------------------------
    // Event handlers
    // -----------------------------------------------------------------------

    #handleChange(e) {
      const input = e.target;

      if (input.classList.contains('ss-drawer-sort-select')) {
        this.#applyFilters();
        const parsed = this.#parseSortValue(input.value);
        document.dispatchEvent(
          new CustomEvent('ss:sort-changed', { detail: parsed })
        );
        return;
      }

      if (input.name && input.name.startsWith('filter.') && !input.dataset.priceRange) {
        this.#applyFilters();
        document.dispatchEvent(
          new CustomEvent('ss:filter-changed', {
            detail: {
              field: input.name,
              value: input.value,
              action: input.checked ? 'add' : 'remove',
            },
          })
        );
      }
    }

    #handlePriceInput(e) {
      const input = e.target;
      if (!input.dataset.priceRange) return;

      clearTimeout(this.#priceDebounceTimer);
      this.#priceDebounceTimer = setTimeout(() => {
        this.#applyFilters();
        document.dispatchEvent(
          new CustomEvent('ss:filter-changed', {
            detail: { field: 'ss_price', value: input.value, action: 'add' },
          })
        );
      }, 300);
    }

    #handlePillRemove(e) {
      const pill = e.target.closest('[data-filter-field]');
      if (!pill) return;

      const field = pill.dataset.filterField;
      const value = pill.dataset.filterValue;

      const input = this.#bodyEl.querySelector(
        'input[name="filter.' + CSS.escape(field) + '"][value="' + CSS.escape(value) + '"]'
      );
      if (input) input.checked = false;

      this.#applyFilters();
      document.dispatchEvent(
        new CustomEvent('ss:filter-changed', {
          detail: { field, value, action: 'remove' },
        })
      );
    }

    // -----------------------------------------------------------------------
    // Filter application
    // -----------------------------------------------------------------------

    #applyFilters() {
      const params = this.#buildParamsFromDOM();
      window.SSURLState.writeToURL(params);
      (window.__ssCollection || window.__ssSearch).refresh(params);
    }

    #buildParamsFromDOM() {
      const params = {};

      this.#bodyEl.querySelectorAll('input[type="checkbox"][name^="filter."]:checked').forEach((input) => {
        const name = input.name;
        if (params[name] !== undefined) {
          if (Array.isArray(params[name])) {
            params[name].push(input.value);
          } else {
            params[name] = [params[name], input.value];
          }
        } else {
          params[name] = input.value;
        }
      });

      const minInput = this.#bodyEl.querySelector('input[data-price-range="min"]');
      const maxInput = this.#bodyEl.querySelector('input[data-price-range="max"]');
      if (minInput && minInput.value) params['filter.ss_price.low'] = minInput.value;
      if (maxInput && maxInput.value) params['filter.ss_price.high'] = maxInput.value;

      const sortSelect = this.#bodyEl.querySelector('.ss-drawer-sort-select');
      if (sortSelect && sortSelect.value) {
        const parsed = this.#parseSortValue(sortSelect.value);
        if (parsed.field !== 'relevance') {
          params['sort.' + parsed.field] = parsed.direction;
        }
      }

      return params;
    }

    #clearAll() {
      this.#bodyEl.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
        input.checked = false;
      });
      this.#bodyEl.querySelectorAll('input[data-price-range]').forEach((input) => {
        input.value = '';
      });
      const sortSelect = this.#bodyEl.querySelector('.ss-drawer-sort-select');
      if (sortSelect) sortSelect.selectedIndex = 0;

      this.#applyFilters();
    }

    #parseSortValue(value) {
      const parts = value.split('_');
      const direction = parts.pop();
      const field = parts.join('_');
      return { field, direction };
    }

    // -----------------------------------------------------------------------
    // Footer & trigger badge
    // -----------------------------------------------------------------------

    #updateClearAllVisibility(filterSummary) {
      if (!this.#clearBtn) return;
      this.#clearBtn.classList.toggle('facets__clear-all--active', (filterSummary || []).length > 0);
    }

    #updateFooterCount(response) {
      if (!this.#footerBtn) return;
      const total = response?.pagination?.totalResults;
      if (total !== undefined && total !== null) {
        this.#footerBtn.textContent = 'See ' + total + ' item' + (total !== 1 ? 's' : '');
      } else {
        this.#footerBtn.textContent = 'See items';
      }
    }

    #updateTriggerBadge(response) {
      const btn = document.querySelector('.ss-mobile-filter-trigger');
      if (!btn) return;
      const count = (response?.filterSummary || []).length;
      const existing = btn.querySelector('.filter-count-bubble');

      if (count > 0 && !existing) {
        const bubble = document.createElement('div');
        bubble.className = 'filter-count-bubble';
        bubble.innerHTML =
          '<span class="filter-count-bubble__background"></span>' +
          '<span class="filter-count-bubble__text">' + count + '</span>';
        btn.appendChild(bubble);
      } else if (count > 0 && existing) {
        existing.querySelector('.filter-count-bubble__text').textContent = count;
      } else if (count === 0 && existing) {
        existing.remove();
      }
    }
  }

  window.SSMobileDrawer = SSMobileDrawer;
})();
